import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut,
  Search,
  Phone,
  Mail,
  Trash2,
  CheckCircle2,
  Clock,
  Circle,
  MessageSquare,
  Loader2,
  RefreshCw,
  Filter,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { services } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: "New", color: "bg-brand-yellow text-brand-dark", icon: Circle },
  contacted: { label: "Contacted", color: "bg-blue-500 text-white", icon: Clock },
  closed: { label: "Closed", color: "bg-green-600 text-white", icon: CheckCircle2 },
};

const AdminDashboard = () => {
  const { isAuthenticated, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout]);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (serviceFilter !== "all") params.service = serviceFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/admin/quotes`, { params });
      setQuotes(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
      } else {
        toast({ title: "Load failed", description: e?.message, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, serviceFilter, search, logout, toast]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchStats();
    fetchQuotes();
  }, [isAuthenticated, fetchStats, fetchQuotes]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status: newStatus });
      toast({ title: "Status updated", description: `Marked as ${statusConfig[newStatus].label}` });
      fetchStats();
      fetchQuotes();
    } catch (e) {
      toast({ title: "Update failed", description: e?.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/admin/quotes/${deleteId}`);
      toast({ title: "Quote deleted" });
      setDeleteId(null);
      fetchStats();
      fetchQuotes();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.message, variant: "destructive" });
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-brand-dark border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_design-build-live-1/artifacts/pt3xqgqe_file_000000007bb48210bf689d869ac39baf_GEoBTL.png"
              alt="KJ"
              className="h-14 w-auto object-contain"
            />
            <div className="hidden sm:block leading-tight border-l border-white/10 pl-4">
              <div className="text-brand-yellow text-[10px] font-bold tracking-[0.2em]">ADMIN PORTAL</div>
              <div className="text-white font-display text-lg tracking-wide">QUOTES DASHBOARD</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => { fetchStats(); fetchQuotes(); }}
              className="text-white hover:bg-white/10 hover:text-brand-yellow"
            >
              <RefreshCw size={16} className="mr-2" />
              <span className="hidden sm:inline">REFRESH</span>
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-brand-yellow hover:bg-brand-yellow-dark text-brand-dark font-bold tracking-wider text-xs"
            >
              <LogOut size={14} className="mr-2" />
              LOGOUT
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="TOTAL QUOTES" value={stats.total} accent="brand-dark" iconBg="bg-brand-yellow" icon={<MessageSquare size={22} className="text-brand-dark" />} />
          <StatCard label="NEW" value={stats.new} accent="brand-yellow" iconBg="bg-brand-yellow" icon={<Circle size={22} className="text-brand-dark" />} />
          <StatCard label="CONTACTED" value={stats.contacted} accent="blue-500" iconBg="bg-blue-500" icon={<Clock size={22} className="text-white" />} />
          <StatCard label="CLOSED" value={stats.closed} accent="green-600" iconBg="bg-green-600" icon={<CheckCircle2 size={22} className="text-white" />} />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by name, email, phone or message..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 border-gray-200"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full lg:w-48 h-11 border-gray-200">
                <Filter size={14} className="mr-2 text-gray-400" />
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-full lg:w-64 h-11 border-gray-200">
                <SelectValue placeholder="All services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Quotes list */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Loader2 size={32} className="animate-spin mb-3" />
            <p className="text-sm">Loading quotes...</p>
          </div>
        ) : quotes.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-100 p-16 text-center">
            <MessageSquare size={40} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-display text-xl text-brand-dark mb-2">No quotes match your filters</h3>
            <p className="text-gray-500 text-sm">Try adjusting the search or filters above.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {quotes.map((q) => (
              <QuoteCard
                key={q.id}
                q={q}
                onStatusChange={handleStatusChange}
                onDelete={() => setDeleteId(q.id)}
                fmtDate={fmtDate}
              />
            ))}
          </div>
        )}
      </main>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the quote from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatCard = ({ label, value, iconBg, icon }) => (
  <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 ${iconBg} rounded-md flex items-center justify-center shrink-0`}>{icon}</div>
    <div>
      <div className="text-[10px] font-bold text-gray-500 tracking-[0.2em]">{label}</div>
      <div className="font-display text-3xl text-brand-dark leading-none mt-1">{value}</div>
    </div>
  </div>
);

const QuoteCard = ({ q, onStatusChange, onDelete, fmtDate }) => {
  const cfg = statusConfig[q.status] || statusConfig.new;
  const StatusIcon = cfg.icon;
  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="font-display text-lg text-brand-dark tracking-wide">{q.name}</h3>
              <Badge className={`${cfg.color} text-[10px] font-bold tracking-wider px-2 py-0.5 hover:${cfg.color}`}>
                <StatusIcon size={10} className="mr-1" />
                {cfg.label.toUpperCase()}
              </Badge>
              {q.service && (
                <Badge variant="outline" className="text-[10px] font-bold tracking-wider border-brand-yellow text-brand-yellow bg-brand-yellow/5">
                  {q.service}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 text-gray-600 hover:text-brand-yellow transition-colors">
                <Mail size={13} />
                <span className="truncate max-w-[220px]">{q.email}</span>
              </a>
              {q.phone && (
                <a href={`tel:${q.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-gray-600 hover:text-brand-yellow transition-colors">
                  <Phone size={13} />
                  <span>{q.phone}</span>
                </a>
              )}
              <span className="text-gray-400 text-xs flex items-center">
                <Clock size={12} className="mr-1" />
                {fmtDate(q.created_at)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border-l-4 border-brand-yellow rounded-r-md p-4 mb-4">
          <div className="text-[10px] font-bold text-brand-dark tracking-[0.2em] mb-1.5">MESSAGE</div>
          <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed">{q.message}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
          <a href={`tel:${(q.phone || "").replace(/\s/g, "")}`}>
            <Button size="sm" variant="outline" className="border-gray-200 hover:bg-brand-yellow hover:border-brand-yellow hover:text-brand-dark" disabled={!q.phone}>
              <Phone size={13} className="mr-1.5" /> Call
            </Button>
          </a>
          <a href={`mailto:${q.email}?subject=Re: Your quote request - KJ Ground Force`}>
            <Button size="sm" variant="outline" className="border-gray-200 hover:bg-brand-yellow hover:border-brand-yellow hover:text-brand-dark">
              <Mail size={13} className="mr-1.5" /> Reply
            </Button>
          </a>
          <div className="flex-1" />
          {q.status !== "contacted" && (
            <Button size="sm" onClick={() => onStatusChange(q.id, "contacted")} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Clock size={13} className="mr-1.5" /> Mark Contacted
            </Button>
          )}
          {q.status !== "closed" && (
            <Button size="sm" onClick={() => onStatusChange(q.id, "closed")} className="bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle2 size={13} className="mr-1.5" /> Mark Closed
            </Button>
          )}
          {q.status !== "new" && (
            <Button size="sm" variant="outline" onClick={() => onStatusChange(q.id, "new")} className="border-gray-200 hover:bg-gray-100">
              Reopen
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onDelete} className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700">
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
